import { useEffect, useState } from "react";

import { Loader2, Trash2, Unlink } from "lucide-react";
import Swal from "sweetalert2";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { formatCurrency } from "~/utils/helpers";

interface Payment {
  id: string;
  amount: number;
  client: string;
  payment_method: string;
  created_at: string;
  type: "sales_request" | "manual_factura";
  documentInfo: string;
  clientName: string;
  documentId: string;
}

interface BankTransaction {
  id: string;
  amount: number;
  description: string;
  transaction_date: string;
  sender_account_name: string;
  sender_account_holder_id: string;
  total_assigned?: number;
  remaining_balance?: number;
}

interface UnlinkPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: BankTransaction;
  onPaymentsUnlinked: (unlinkedAmount: number) => void;
}

/** Loading placeholder that mirrors the payments-list layout. */
function PaymentsListSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="rounded-md border p-3">
            <div className="flex items-start gap-3">
              <Skeleton className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-24 rounded-full" />
                  <Skeleton className="h-5 w-20" />
                </div>
                <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UnlinkPaymentDialog({
  open,
  onOpenChange,
  transaction,
  onPaymentsUnlinked,
}: UnlinkPaymentDialogProps) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [selectedPayments, setSelectedPayments] = useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    if (open && transaction) {
      fetchPayments();
    }
  }, [open, transaction]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      // Get all payments for this movement
      const { data: allPayments, error } = await supabase
        .from("payments")
        .select(
          `
          id,
          amount,
          client,
          payment_method,
          created_at,
          sales_request,
          manual_factura_id,
          clients!inner(name),
          sales_request(id, total_amount, sales_request_documents!sales_request_id(bsale_number, document_type)),
          manual_facturas(id, doc_number, doc_type)
        `
        )
        .eq("rail_movement_id", transaction.id);

      if (error && !error.message.includes("manual_factura_id")) {
        throw error;
      }

      const formattedPayments: Payment[] = (allPayments || [])
        .map((payment: any) => {
          if (payment.sales_request) {
            return {
              id: payment.id,
              amount: payment.amount,
              client: payment.client,
              payment_method: payment.payment_method,
              created_at: payment.created_at,
              type: "sales_request" as const,
              documentInfo: (() => {
                const facturaDoc =
                  payment.sales_request.sales_request_documents?.find(
                    (doc: any) =>
                      doc.document_type === "factura" && doc.bsale_number
                  );
                return facturaDoc?.bsale_number
                  ? `Factura ${facturaDoc.bsale_number}`
                  : `Nota de Venta ${payment.sales_request.id?.substring(0, 8)}...`;
              })(),
              clientName: payment.clients?.name || "Cliente desconocido",
              documentId: payment.sales_request.id,
            };
          } else if (payment.manual_facturas) {
            return {
              id: payment.id,
              amount: payment.amount,
              client: payment.client,
              payment_method: payment.payment_method,
              created_at: payment.created_at,
              type: "manual_factura" as const,
              documentInfo: `${payment.manual_facturas.doc_type || "FV"} ${payment.manual_facturas.doc_number || "Sin número"}`,
              clientName: payment.clients?.name || "Cliente desconocido",
              documentId: payment.manual_facturas.id,
            };
          }
          return null;
        })
        .filter(Boolean);

      setPayments(formattedPayments);
    } catch (err) {
      console.error("Error fetching payments:", err);
      toast({
        title: "Error",
        description: "No se pudieron cargar los pagos asociados",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSelect = (paymentId: string, checked: boolean) => {
    const newSelected = new Set(selectedPayments);
    if (checked) {
      newSelected.add(paymentId);
    } else {
      newSelected.delete(paymentId);
    }
    setSelectedPayments(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPayments(new Set(payments.map((p) => p.id)));
    } else {
      setSelectedPayments(new Set());
    }
  };

  const getSelectedAmount = () => {
    return payments
      .filter((p) => selectedPayments.has(p.id))
      .reduce((sum, p) => sum + p.amount, 0);
  };

  const handleUnlinkSelected = async () => {
    if (selectedPayments.size === 0) {
      toast({
        title: "Error",
        description: "Seleccione al menos un pago para desvincular",
        variant: "destructive",
      });
      return;
    }

    const selectedAmount = getSelectedAmount();
    const selectedPaymentsList = payments.filter((p) =>
      selectedPayments.has(p.id)
    );

    // Show confirmation modal
    const result = await Swal.fire({
      title: "¿Desvincular pagos seleccionados?",
      html: `
        <div class="text-left">
          <p class="mb-2">¿Está seguro que desea desvincular ${selectedPayments.size} pago(s)?</p>
          <div class="bg-gray-50 p-3 rounded border mb-3">
            <p><strong>Monto total a desvincular:</strong> $${formatCurrency(selectedAmount)}</p>
            <p><strong>Pagos seleccionados:</strong></p>
            <ul class="text-sm mt-1">
              ${selectedPaymentsList
                .map(
                  (p) => `
                <li>• $${formatCurrency(p.amount)} - ${p.documentInfo} (${p.clientName})</li>
              `
                )
                .join("")}
            </ul>
          </div>
          <p class="text-sm text-gray-600">Esta acción eliminará la vinculación de los pagos seleccionados con sus documentos.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, desvincular",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      customClass: {
        container: "swal2-z-top",
        popup: "z-[100000]",
      },
      // Strong mobile isolation
      allowOutsideClick: false,
      allowEscapeKey: false,
      backdrop: `rgba(0,0,0,0.6)`,
      heightAuto: false,
      scrollbarPadding: false,
      stopKeydownPropagation: true,
      focusConfirm: true,
      buttonsStyling: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setUnlinking(true);
    try {
      let unlinkedAmount = 0;

      for (const payment of selectedPaymentsList) {
        if (payment.type === "sales_request") {
          // Handle sales_request unlinking with direct database operations

          // First, get the current sales request data
          const { data: salesRequestData, error: fetchError } = await supabase
            .from("sales_request")
            .select("paid_amount")
            .eq("id", payment.documentId)
            .single();

          if (fetchError) {
            console.error("Error fetching sales request:", fetchError);
            throw new Error(
              `Error al obtener datos de la nota de venta ${payment.documentInfo}`
            );
          }

          // Calculate new paid amount
          const currentPaid = salesRequestData.paid_amount || 0;
          const newPaidAmount = Math.max(0, currentPaid - payment.amount);

          // Delete the payment record
          const { error: deleteError } = await supabase
            .from("payments")
            .delete()
            .eq("id", payment.id);

          if (deleteError) {
            console.error("Error deleting payment:", deleteError);
            throw new Error(
              `Error al eliminar el pago de ${payment.documentInfo}`
            );
          }

          // Update the sales request with new paid amount
          const { error: updateError } = await supabase
            .from("sales_request")
            .update({
              paid_amount: newPaidAmount,
            })
            .eq("id", payment.documentId);

          if (updateError) {
            console.error("Error updating sales request:", updateError);
            throw new Error(
              `Error al actualizar la nota de venta ${payment.documentInfo}`
            );
          }

          unlinkedAmount += payment.amount;
        } else if (payment.type === "manual_factura") {
          // Handle manual_factura unlinking with direct database operations

          // First, get the manual factura data
          const { data: facturaData, error: fetchError } = await supabase
            .from("manual_facturas")
            .select("paid, dues, balance")
            .eq("id", payment.documentId)
            .single();

          if (fetchError) {
            console.error("Error fetching manual factura:", fetchError);
            throw new Error(
              `Error al obtener datos de la factura ${payment.documentInfo}`
            );
          }

          // Calculate new paid amount and balance
          const currentPaid = facturaData.paid || 0;
          const newPaidAmount = Math.max(0, currentPaid - payment.amount);
          const duesAmount = facturaData.dues || 0;
          const newBalance = duesAmount - newPaidAmount;

          // Delete the payment record
          const { error: deleteError } = await supabase
            .from("payments")
            .delete()
            .eq("id", payment.id);

          if (deleteError) {
            console.error("Error deleting payment:", deleteError);
            throw new Error(
              `Error al eliminar el pago de ${payment.documentInfo}`
            );
          }

          // Update the manual factura with new paid amount and balance
          const { error: updateError } = await supabase
            .from("manual_facturas")
            .update({
              paid: newPaidAmount,
              balance: newBalance,
            })
            .eq("id", payment.documentId);

          if (updateError) {
            console.error("Error updating manual factura:", updateError);
            throw new Error(
              `Error al actualizar la factura ${payment.documentInfo}`
            );
          }

          unlinkedAmount += payment.amount;
        }
      }

      toast({
        title: "Pagos desvinculados",
        description: `Se desvincularon ${selectedPayments.size} pago(s) por un total de $${formatCurrency(unlinkedAmount)}`,
      });

      onPaymentsUnlinked(unlinkedAmount);
      onOpenChange(false);
    } catch (err) {
      console.error("Error unlinking payments:", err);
      toast({
        title: "Error",
        description:
          err instanceof Error
            ? err.message
            : "No se pudieron desvincular algunos pagos",
        variant: "destructive",
      });
    } finally {
      setUnlinking(false);
    }
  };

  const allSelected =
    selectedPayments.size === payments.length && payments.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent
        className="flex max-h-[85vh] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
        style={{ zIndex: 9998 }}
      >
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2">
            <Unlink className="h-5 w-5" />
            Desvincular Pagos
          </DialogTitle>
          <DialogDescription>
            Seleccione los pagos que desea desvincular de este movimiento
            bancario.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {/* Transaction summary */}
          <div className="rounded-lg border bg-muted/40 p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">
                  Monto total
                </p>
                <p className="text-sm font-semibold tabular-nums">
                  ${formatCurrency(transaction.amount)}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">
                  Ya asignado
                </p>
                <p
                  className={`text-sm font-semibold tabular-nums ${
                    (transaction.total_assigned || 0) > 0
                      ? "text-green-700"
                      : ""
                  }`}
                >
                  ${formatCurrency(transaction.total_assigned || 0)}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">
                  Disponible
                </p>
                <p className="text-sm font-semibold tabular-nums">
                  ${formatCurrency(transaction.remaining_balance || 0)}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">
                  Remitente
                </p>
                <p className="truncate text-sm font-medium">
                  {transaction.sender_account_name || "Desconocido"}
                </p>
              </div>
            </div>
          </div>

          {/* Payments List */}
          {loading ? (
            <PaymentsListSkeleton />
          ) : payments.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              No se encontraron pagos asociados a este movimiento.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">
                  Pagos Asignados ({payments.length})
                </h4>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all"
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                  />
                  <label
                    htmlFor="select-all"
                    className="cursor-pointer text-sm font-medium"
                  >
                    Seleccionar todos
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                {payments.map((payment) => {
                  const isSelected = selectedPayments.has(payment.id);
                  return (
                    <div
                      key={payment.id}
                      className={`cursor-pointer rounded-md border p-3 transition-colors ${
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-input hover:bg-muted/40"
                      }`}
                      onClick={() =>
                        handlePaymentSelect(payment.id, !isSelected)
                      }
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id={`payment-${payment.id}`}
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handlePaymentSelect(payment.id, checked as boolean)
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="mb-1.5 flex items-center gap-2">
                            <Badge variant="secondary">
                              {payment.type === "sales_request"
                                ? "Nota de Venta"
                                : "Factura Manual"}
                            </Badge>
                            <span className="text-base font-semibold tabular-nums">
                              ${formatCurrency(payment.amount)}
                            </span>
                          </div>
                          <div className="grid gap-x-4 gap-y-0.5 text-sm text-muted-foreground sm:grid-cols-2">
                            <p className="truncate">
                              <span className="font-medium text-foreground">
                                Cliente:
                              </span>{" "}
                              {payment.clientName}
                            </p>
                            <p className="truncate">
                              <span className="font-medium text-foreground">
                                Documento:
                              </span>{" "}
                              {payment.documentInfo}
                            </p>
                            <p className="truncate">
                              <span className="font-medium text-foreground">
                                Fecha:
                              </span>{" "}
                              {new Date(payment.created_at).toLocaleDateString(
                                "es-ES"
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedPayments.size > 0 && (
                <div className="flex items-center justify-between rounded-md border bg-muted/40 p-3 text-sm">
                  <span className="font-medium">
                    {selectedPayments.size} pago(s) seleccionado(s)
                  </span>
                  <span className="font-semibold tabular-nums">
                    ${formatCurrency(getSelectedAmount())}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            variant="destructive"
            onClick={handleUnlinkSelected}
            disabled={selectedPayments.size === 0 || unlinking}
          >
            {unlinking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Desvinculando...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Desvincular{" "}
                {selectedPayments.size > 0 ? `(${selectedPayments.size})` : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
